import { Dialog } from "@headlessui/react";
import { AnimatePresence } from "motion/react";
import { useMemo, useState } from "react";
import { Overlay } from "../../overlay";
import { useLightboxContainer } from "../context";
import type { GetFileAnnotations, SaveFileAnnotations } from "../shared";
import { LightboxController } from "./lightbox-controller";
import { LightboxFocusItem } from "./lightbox-focus-item";
import { LightboxModalMenu } from "./lightbox-modal-menu";
import { LightboxScrollerItem } from "./lightbox-scroller-item";
import type { LightboxAnnotationControls, LightboxItem } from "./shared";

/** Props Interface */
interface LightboxModalProps {
	isOpen: boolean;
	onClose: () => void;
	itemId: string;
	defaultIsGlobal?: boolean;
	withGlobalToggle?: boolean;
	getAnnotations?: GetFileAnnotations;
	onSaveAnnotations?: SaveFileAnnotations;
}

export function LightboxModal({
	isOpen,
	onClose,
	itemId,
	defaultIsGlobal = false,
	withGlobalToggle = true,
	getAnnotations,
	onSaveAnnotations,
}: LightboxModalProps) {
	/** State */
	const [currentItemId, setCurrentItemId] = useState<string>(itemId);
	const [isGlobal, setIsGlobal] = useState<boolean>(defaultIsGlobal);
	const [annotationControls, setAnnotationControls] = useState<LightboxAnnotationControls | null>(
		null,
	);
	const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);

	/** Context */
	const { containerId } = useLightboxContainer();

	/** Computed */
	const isEditLocked = annotationControls?.isEditing ?? false;

	/** Data */
	const items = useMemo(() => {
		try {
			const query = isGlobal
				? "[data-lightbox-item]"
				: `.lightbox-container-${containerId} [data-lightbox-item]`;

			const elements = document.querySelectorAll(query);
			const queryResult = Array.from(elements).map((el) => {
				const item: LightboxItem = {
					src: el.getAttribute("data-src") || "",
					height: el.getAttribute("data-height") || "",
					width: el.getAttribute("data-width") || "",
					contentType: el.getAttribute("data-content-type") || "",
					itemId: el.getAttribute("data-item-id") || "",
					fileName: el.getAttribute("data-file-name") || "",
				};
				return item;
			});

			if (!queryResult.some((item) => item.itemId === currentItemId)) {
				setCurrentItemId(queryResult[0]?.itemId || "");
			}

			return queryResult;
		} catch (error) {
			console.error("Error parsing lightbox items:", error);
			return [];
		}
	}, [containerId, isGlobal, itemId]);

	const currentItem = useMemo(() => {
		return items.find((item) => item.itemId === currentItemId);
	}, [items, currentItemId]);

	/** Computed */
	function focusPreviousItem() {
		if (!items || isEditLocked) return;
		const currentIndex = items.findIndex((item) => item.itemId === currentItemId);
		const newIndex = (currentIndex - 1 + items.length) % items.length;
		const newItem = items[newIndex];
		if (newItem) {
			setCurrentItemId(newItem.itemId);
		}
	}

	function focusNextItem() {
		if (!items || isEditLocked) return;
		const currentIndex = items.findIndex((item) => item.itemId === currentItemId);
		const newIndex = (currentIndex + 1) % items.length;
		const newItem = items[newIndex];
		if (newItem) {
			setCurrentItemId(newItem.itemId);
		}
	}

	function handleOnRequestClose() {
		if (isEditLocked) return;
		onClose();
	}

	/** Render */
	return (
		<Dialog
			open={isOpen}
			onClose={handleOnRequestClose}
		>
			<Overlay
				onClick={handleOnRequestClose}
				className="z-99998"
			/>
			<div
				className="fixed inset-0 w-screen h-screen max-h-screen max-w-screen grid z-99999 gap-y-0 cursor-pointer grow-0"
				style={{ gridTemplateRows: "max-content 1fr max-content" }}
				onClick={handleOnRequestClose}
			>
				<LightboxModalMenu
					onClose={onClose}
					isGlobal={isGlobal}
					setIsGlobal={setIsGlobal}
					withGlobalToggle={withGlobalToggle}
					annotationControls={annotationControls}
				/>
				<div className="flex-1 flex flex-col items-center relative overflow-hidden">
					<AnimatePresence mode="wait">
						<LightboxFocusItem
							key={currentItemId}
							currentItem={currentItem}
							getAnnotations={getAnnotations}
							onSaveAnnotations={onSaveAnnotations}
							onControlsChange={setAnnotationControls}
							footerSlot={footerSlot}
						/>
					</AnimatePresence>
					{!isEditLocked && (
						<LightboxController
							focusPreviousItem={focusPreviousItem}
							focusNextItem={focusNextItem}
						/>
					)}
				</div>
				<div className="p-8 flex-center">
					{isEditLocked ? (
						<div
							ref={setFooterSlot}
							className="w-full flex-center"
						/>
					) : (
						<div className="flex flex-row gap-2 flex-center overflow-x-auto max-w-full">
							{items.map((item) => {
								return (
									<LightboxScrollerItem
										key={item.itemId}
										item={item}
										currentItemId={currentItemId}
										onClick={() => {
											setCurrentItemId(item.itemId);
										}}
									/>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</Dialog>
	);
}
